import { normalizeItemSearchText } from "./itemSearchText";

/**
 * Display helpers for personnel names with optional nickname (PLAN §2).
 */

/**
 * Normalize all-digit personal numbers so assignment map keys match personnel docs
 * (e.g. "06068128" vs "6068128"). Non-numeric IDs (emails) are returned trimmed as-is.
 * @param {string|number} value
 * @returns {string}
 */
export function canonicalPersonalNumberKey(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (!s) return "";
  if (!/^\d+$/.test(s)) return s;
  const n = parseInt(s, 10);
  return Number.isNaN(n) ? s : String(n);
}

/**
 * Match Firestore/auth IDs to a personnel row (personalNumber may be number or string).
 * @param {Array<Object>} personnelList
 * @param {string|number} personnelId — personal number or email
 * @returns {Object|null}
 */
export function findPersonnelById(personnelList, personnelId) {
  if (personnelId == null || personnelId === "" || !personnelList?.length) {
    return null;
  }
  const idNorm = String(personnelId).trim();
  const idCanon = canonicalPersonalNumberKey(personnelId);
  const idLower = idNorm.toLowerCase();
  for (const p of personnelList) {
    const pn =
      p.personalNumber != null ? String(p.personalNumber).trim() : "";
    const pnCanon =
      p.personalNumber != null ? canonicalPersonalNumberKey(p.personalNumber) : "";
    if (pn && (pn === idNorm || (pnCanon && idCanon && pnCanon === idCanon))) {
      return p;
    }
    const em = (p.email || "").trim().toLowerCase();
    if (em && em === idLower) return p;
  }
  return null;
}

/**
 * Assignee(s) for "חתום ל" on the public employee assignment page — mirrors
 * child-user inventory: delegated recipients in assignments.second first, else first[viewer], else second[viewer].
 * @param {Object} item — inventory document
 * @param {string|number} pagePersonnelId — employeePages.personnelId (page owner)
 * @returns {Array<{ personalNumber: string, firstName: string, lastName: string }>}
 */
export function getAssigneeEntriesForEmployeeAssignmentPage(
  item,
  pagePersonnelId,
) {
  if (!item?.assignments || pagePersonnelId == null || pagePersonnelId === "") {
    return [];
  }
  const pn = String(pagePersonnelId).trim();
  const pnCanon = canonicalPersonalNumberKey(pagePersonnelId);
  const firstMap = item.assignments.first || {};
  const secondMap = item.assignments.second || {};

  const pickAssignmentEntry = (map) => {
    if (!map || typeof map !== "object") return undefined;
    if (map[pn] !== undefined) return map[pn];
    if (pnCanon !== pn && map[pnCanon] !== undefined) return map[pnCanon];
    for (const key of Object.keys(map)) {
      if (canonicalPersonalNumberKey(key) === pnCanon) return map[key];
    }
    return undefined;
  };

  const delegatedOut = Object.entries(secondMap)
    .filter(([, assignment]) => {
      const by = assignment?.assignedBy;
      if (by == null) return false;
      const byStr = String(by).trim();
      return (
        byStr === pn ||
        (pnCanon &&
          canonicalPersonalNumberKey(by) === pnCanon)
      );
    })
    .map(([recipientPn, a]) => ({
      personalNumber: String(recipientPn).trim(),
      firstName: (a?.firstName ?? "").toString(),
      lastName: (a?.lastName ?? "").toString(),
    }));

  if (delegatedOut.length > 0) {
    return delegatedOut;
  }

  const firstEntry = pickAssignmentEntry(firstMap);
  if (firstEntry) {
    const holderPn =
      Object.keys(firstMap).find(
        (k) => firstMap[k] === firstEntry,
      ) || pn;
    return [
      {
        personalNumber: String(holderPn).trim(),
        firstName: (firstEntry.firstName ?? "").toString(),
        lastName: (firstEntry.lastName ?? "").toString(),
      },
    ];
  }

  const secondAsRecipient = pickAssignmentEntry(secondMap);
  if (secondAsRecipient) {
    const holderPn =
      Object.keys(secondMap).find(
        (k) => secondMap[k] === secondAsRecipient,
      ) || pn;
    return [
      {
        personalNumber: String(holderPn).trim(),
        firstName: (secondAsRecipient.firstName ?? "").toString(),
        lastName: (secondAsRecipient.lastName ?? "").toString(),
      },
    ];
  }

  return [];
}

/**
 * @param {Object} p
 * @param {string} [p.firstName]
 * @param {string} [p.lastName]
 * @param {string} [p.nickname]
 * @returns {string} "First Last" or "First Last (nickname)" when nickname is set
 */
export function formatPersonDisplayName(p) {
  if (!p) return "";
  const first = (p.firstName || "").trim();
  const last = (p.lastName || "").trim();
  const nick = (p.nickname || "").trim();
  const base = [first, last].filter(Boolean).join(" ").trim();
  if (!base) return nick || "";
  if (!nick) return base;
  return `${base} (${nick})`;
}

/**
 * @param {Array<{ personalNumber?: string, firstName?: string, lastName?: string }>} entries
 * @param {Map<string, string>} nicknameByPersonalNumber — keys as string personal numbers
 * @param {Array<Object>} [personnelList] — optional; used to resolve nickname when map misses
 * @returns {string} Comma-separated assignee display names
 */
export function formatAssignedPersonnelList(
  entries,
  nicknameByPersonalNumber,
  personnelList,
) {
  if (!entries || !entries.length) return "";
  const map = nicknameByPersonalNumber || new Map();
  return entries
    .map((entry) => {
      const pn =
        entry.personalNumber != null ? String(entry.personalNumber).trim() : "";
      const pnCanon = pn ? canonicalPersonalNumberKey(entry.personalNumber) : "";
      let nickFromPersonnel = pn
        ? (
            map.get(pn) ||
            (pnCanon && pnCanon !== pn ? map.get(pnCanon) : "") ||
            ""
          ).trim()
        : "";
      if (!nickFromPersonnel && personnelList?.length && entry.personalNumber != null) {
        const rec = findPersonnelById(personnelList, entry.personalNumber);
        nickFromPersonnel = (rec?.nickname || "").trim();
      }
      return formatPersonDisplayName({
        firstName: entry.firstName,
        lastName: entry.lastName,
        nickname: nickFromPersonnel || (entry.nickname || "").trim(),
      });
    })
    .filter(Boolean)
    .join(", ");
}

/**
 * @param {Array<{ personalNumber?: string, nickname?: string }>} personnel
 * @returns {Map<string, string>} personalNumber string → trimmed nickname
 */
export function buildNicknameByPersonalNumberMap(personnel) {
  const m = new Map();
  if (!personnel || !Array.isArray(personnel)) return m;
  personnel.forEach((p) => {
    const pn =
      p?.personalNumber != null ? String(p.personalNumber).trim() : "";
    if (!pn) return;
    const nick = (p.nickname || "").trim();
    if (!nick) return;
    m.set(pn, nick);
    const canon = canonicalPersonalNumberKey(p.personalNumber);
    if (canon && canon !== pn) {
      m.set(canon, nick);
    }
  });
  return m;
}

/**
 * Lowercased haystack for searching by assignee (legal name + nickname).
 * @param {Array<{ personalNumber?: string, firstName?: string, lastName?: string }>} entries
 * @param {Map<string, string>} nicknameByPersonalNumber
 * @param {Array<Object>} [personnelList]
 */
export function buildAssigneeSearchHaystack(
  entries,
  nicknameByPersonalNumber,
  personnelList,
) {
  if (!entries || !entries.length) return "";
  const map = nicknameByPersonalNumber || new Map();
  const parts = [];
  entries.forEach((entry) => {
    const first = normalizeItemSearchText(entry.firstName);
    const last = normalizeItemSearchText(entry.lastName);
    const pn =
      entry.personalNumber != null ? String(entry.personalNumber).trim() : "";
    const pnCanon = pn ? canonicalPersonalNumberKey(entry.personalNumber) : "";
    let nickRaw = pn
      ? map.get(pn) || (pnCanon && pnCanon !== pn ? map.get(pnCanon) : "") || ""
      : "";
    if (!nickRaw && personnelList?.length && entry.personalNumber != null) {
      const rec = findPersonnelById(personnelList, entry.personalNumber);
      nickRaw = (rec?.nickname || "").trim();
    }
    const nick = normalizeItemSearchText(nickRaw).trim();
    if (first) parts.push(first);
    if (last) parts.push(last);
    if (nick) parts.push(nick);
    const combined = [first, last].filter(Boolean).join(" ");
    if (combined) parts.push(combined);
    if (nick && combined) parts.push(`${combined} (${nick})`);
  });
  return parts.join(" ");
}
